import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2 } from "lucide-react";
import { asset } from "../../lib/api";
import { play, sfx } from "../../lib/audio";
import { Speaking } from "./Speaking";

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Shared single-answer logic: retries, attempt limit, then reveal.
const useChoice = (activity, record, onDone) => {
  const [picked, setPicked] = useState({});
  const [solved, setSolved] = useState(false);
  const misses = useRef(0);
  const choose = (opt) => {
    if (solved || picked[opt]) return;
    const ok = opt === activity.correct_answer;
    record(ok);
    if (ok) {
      sfx("good");
      setPicked((p) => ({ ...p, [opt]: "good" }));
      setSolved(true);
      setTimeout(() => onDone(misses.current === 0), 1100);
    } else {
      sfx("bad");
      misses.current += 1;
      setPicked((p) => ({ ...p, [opt]: "bad" }));
      if (misses.current >= (activity.attempt_limit || 3)) {
        setSolved(true);
        setPicked((p) => ({ ...p, [activity.correct_answer]: "reveal" }));
        setTimeout(() => onDone(false), 1800);
      }
    }
  };
  return { picked, choose };
};

const optCls = (st) =>
  st === "good" || st === "reveal" ? "border-[var(--jade)] bg-green-50 ring-4 ring-green-200" : st === "bad" ? "border-red-300 bg-red-50 opacity-60 shake" : "border-[var(--line)] bg-[#FFFDF7] hover:border-amber-400";

const Title = ({ children, audio }) => (
  <div className="flex items-center gap-3 justify-center">
    <h2 className="font-serif-tc text-3xl sm:text-4xl font-black text-[var(--tea)] text-center" data-testid="activity-instruction">{children}</h2>
    {audio && <button data-testid="activity-instruction-audio" onClick={() => play(audio)} className="w-12 h-12 rounded-full bg-amber-100 text-[var(--tea)] flex items-center justify-center"><Volume2 /></button>}
  </div>
);

const Option = ({ st, onClick, children, testid }) => (
  <motion.button data-testid={testid} whileTap={{ scale: 0.92 }} onClick={onClick}
    className={`scroll-card !rounded-[2rem] border-4 p-5 min-w-[150px] flex flex-col items-center gap-2 transition-colors ${optCls(st)}`}>
    {children}
    {(st === "good" || st === "reveal") && <span className="text-3xl">⭐</span>}
  </motion.button>
);

export const ListenChoose = ({ activity, record, onDone }) => {
  const q = activity.question_data || {};
  const opts = useMemo(() => shuffle(activity.answer_options), [activity]);
  const { picked, choose } = useChoice(activity, record, onDone);
  useEffect(() => { setTimeout(() => play(q.prompt_audio, activity.audio_asset), 400); }, [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-8" data-testid="game-listen-choose">
      <Title>{activity.instruction}</Title>
      <motion.button data-testid="listen-play-btn" onClick={() => play(q.prompt_audio, activity.audio_asset)} animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
        className="w-32 h-32 rounded-full bg-[var(--vermilion)] text-white flex flex-col items-center justify-center shadow-xl">
        <Volume2 size={56} />
        {q.show_char && <span className="font-serif-tc text-2xl font-bold">{q.show_char}</span>}
      </motion.button>
      <div className="flex flex-wrap justify-center gap-5">
        {opts.map((o) => (
          <Option key={o} st={picked[o]} onClick={() => choose(o)} testid={`listen-option-${o}`}>
            {q.mode === "zhuyin" ? (
              <span className="text-5xl font-black text-amber-900 py-4">{o}</span>
            ) : q.mode === "picture" ? (
              <span className="text-[5.5rem] leading-none py-2">{q.option_meta?.[o]?.emoji}</span>
            ) : (
              <>
                <span className="text-5xl">{q.option_meta?.[o]?.emoji}</span>
                <span className="font-serif-tc text-6xl font-black text-red-950">{o}</span>
              </>
            )}
          </Option>
        ))}
      </div>
    </div>
  );
};

export const ZhuyinMatch = ({ activity, record, onDone }) => {
  const q = activity.question_data || {};
  const opts = useMemo(() => shuffle(activity.answer_options), [activity]);
  const { picked, choose } = useChoice(activity, record, onDone);
  useEffect(() => { setTimeout(() => play(q.audio_text, q.audio_url), 400); }, [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-8" data-testid="game-zhuyin-match">
      <Title>{activity.instruction}</Title>
      <motion.button data-testid="zhuyin-match-symbol" whileTap={{ scale: 0.9 }} onClick={() => play(q.audio_text, q.audio_url)}
        className="w-36 h-36 rounded-[2rem] bg-amber-100 border-4 border-amber-300 text-8xl font-black text-amber-900 shadow-lg">{q.symbol}</motion.button>
      <div className="flex flex-wrap justify-center gap-5">
        {opts.map((o) => (
          <Option key={o} st={picked[o]} onClick={() => { play(o); choose(o); }} testid={`zhuyin-match-option-${o}`}>
            <span className="text-5xl">{q.option_meta?.[o]?.emoji}</span>
            <span className="font-serif-tc text-6xl font-black text-red-950">{o}</span>
          </Option>
        ))}
      </div>
    </div>
  );
};

export const Comprehension = ({ activity, record, onDone }) => {
  const q = activity.question_data || {};
  const opts = useMemo(() => shuffle(q.options || activity.answer_options.map((t) => ({ text: t }))), [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  const { picked, choose } = useChoice(activity, record, onDone);
  useEffect(() => { setTimeout(() => play(q.question || activity.instruction), 400); }, [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-8" data-testid="game-comprehension">
      <Title audio={q.question || activity.instruction}>{q.question || activity.instruction}</Title>
      <div className="flex flex-wrap justify-center gap-5">
        {opts.map((o) => (
          <Option key={o.text} st={picked[o.text]} onClick={() => { play(o.text); choose(o.text); }} testid={`comprehension-option-${o.text}`}>
            <span className="text-7xl">{o.emoji}</span>
            <span className="font-serif-tc text-2xl font-bold">{o.text}</span>
          </Option>
        ))}
      </div>
    </div>
  );
};

export const SceneSearch = ({ activity, record, onDone }) => {
  const q = activity.question_data || {};
  const { picked, choose } = useChoice(activity, record, onDone);
  useEffect(() => { setTimeout(() => play(activity.instruction.replace(/[『』]/g, "")), 400); }, [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-4 w-full" data-testid="game-scene-search">
      <Title audio={activity.instruction.replace(/[『』]/g, "")}>{activity.instruction}</Title>
      <div className="relative w-full max-w-4xl aspect-[3/2] rounded-[2rem] overflow-hidden border-4 border-amber-200 shadow-xl">
        <img src={asset(q.background)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        {(q.objects || []).map((o) => (
          <motion.button key={o.label} data-testid={`scene-search-object-${o.label}`} onClick={() => { play(o.label); choose(o.label); }}
            animate={picked[o.label] === "good" || picked[o.label] === "reveal" ? { scale: [1, 1.4, 1.2] } : picked[o.label] === "bad" ? { x: [0, -10, 10, 0] } : { y: [0, -6, 0] }}
            transition={{ duration: picked[o.label] ? 0.5 : 2.5, repeat: picked[o.label] ? 0 : Infinity }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full p-2 ${picked[o.label] === "good" || picked[o.label] === "reveal" ? "bg-green-200/80 ring-4 ring-green-400" : ""}`}
            style={{ left: `${o.x}%`, top: `${o.y}%`, fontSize: "min(8vw, 12vh)" }}>
            {o.emoji}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export const PictureMatch = ({ activity, record, onDone }) => {
  const pairs = activity.question_data?.pairs || [];
  const pics = useMemo(() => shuffle(pairs), [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  const words = useMemo(() => shuffle(pairs), [activity]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selPic, setSelPic] = useState(null);
  const [matched, setMatched] = useState([]);
  const [bad, setBad] = useState(null);
  const mistakes = useRef(0);

  const tapWord = (w) => {
    if (!selPic || matched.includes(w.word)) return play(w.word);
    const ok = selPic.word === w.word;
    record(ok, [{ type: activity.question_data?.item_type || "character", key: selPic.word, correct: ok }]);
    play(w.word);
    if (ok) {
      sfx("good");
      const m = [...matched, w.word];
      setMatched(m);
      setSelPic(null);
      if (m.length === pairs.length) setTimeout(() => onDone(mistakes.current === 0), 1000);
    } else {
      sfx("bad");
      mistakes.current += 1;
      setBad(w.word);
      setTimeout(() => setBad(null), 600);
    }
  };

  return (
    <div className="flex flex-col items-center gap-8" data-testid="game-picture-match">
      <Title>{activity.instruction}</Title>
      <div className="grid grid-cols-2 gap-10 sm:gap-20">
        <div className="flex flex-col gap-4">
          {pics.map((p) => (
            <motion.button key={p.word} data-testid={`match-pic-${p.word}`} whileTap={{ scale: 0.9 }} disabled={matched.includes(p.word)} onClick={() => { sfx("tap"); setSelPic(p); }}
              className={`w-28 h-24 sm:w-32 sm:h-28 rounded-3xl border-4 text-6xl flex items-center justify-center ${matched.includes(p.word) ? "bg-green-50 border-[var(--jade)] opacity-60" : selPic?.word === p.word ? "bg-amber-100 border-amber-500 scale-105" : "bg-[#FFFDF7] border-[var(--line)]"}`}>
              {p.emoji}
            </motion.button>
          ))}
        </div>
        <div className="flex flex-col gap-4">
          {words.map((w) => (
            <motion.button key={w.word} data-testid={`match-word-${w.word}`} whileTap={{ scale: 0.9 }} onClick={() => tapWord(w)}
              className={`h-24 sm:h-28 rounded-3xl border-4 font-serif-tc font-black flex items-center justify-center ${w.word.length > 1 ? "min-w-[9rem] px-4 text-4xl sm:text-5xl" : "w-28 sm:w-32 text-6xl"} ${matched.includes(w.word) ? "bg-green-50 border-[var(--jade)] text-green-800" : bad === w.word ? "border-red-300 bg-red-50 shake" : "bg-[#FFFDF7] border-[var(--line)] text-red-950"}`}>
              {w.word}
            </motion.button>
          ))}
        </div>
      </div>
      <p className="font-serif-tc text-lg text-stone-500">{selPic ? (activity.question_data?.item_type === "word" ? "現在點一點對的詞" : "現在點一點對的字") : "先點一張圖"}</p>
    </div>
  );
};

export const GAMES = {
  listen_choose: ListenChoose,
  zhuyin_match: ZhuyinMatch,
  comprehension: Comprehension,
  scene_search: SceneSearch,
  picture_match: PictureMatch,
  speaking: Speaking,
};
